import {
  BulbOutlined,
  CheckCircleFilled,
  FileSearchOutlined,
  LoadingOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import React, { useEffect, useMemo, useState } from 'react';
import {
  lineageDemoApi,
  LineageEvidence,
  LineageEvidenceConfidence,
  LineageImprovement,
  LineageInterpretationReport,
  LineageNode,
} from './lineageDemoApi';

interface LineageAiInspectorProps {
  node: LineageNode | null;
  field: string | null;
  onFieldChange: (field: string | null) => void;
}

type NarrativeState = LineageInterpretationReport['narrative'];

const emptyNarrative: NarrativeState = { summary: '', basis: '', confidence: '', improvement: '' };

export const LineageAiInspector: React.FC<LineageAiInspectorProps> = ({ node, field, onFieldChange }) => {
  const [status, setStatus] = useState<'idle' | 'streaming' | 'done' | 'error'>('idle');
  const [confidence, setConfidence] = useState<LineageEvidenceConfidence | null>(null);
  const [narrative, setNarrative] = useState<NarrativeState>(emptyNarrative);
  const [evidence, setEvidence] = useState<LineageEvidence[]>([]);
  const [improvements, setImprovements] = useState<LineageImprovement[]>([]);
  const [authority, setAuthority] = useState<LineageInterpretationReport['authority'] | null>(null);
  const [snapshotId, setSnapshotId] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!node) {
      setStatus('idle');
      return undefined;
    }
    const controller = new AbortController();
    setStatus('streaming');
    setConfidence(null);
    setNarrative(emptyNarrative);
    setEvidence([]);
    setImprovements([]);
    setAuthority(null);
    setError(null);
    void lineageDemoApi.streamInterpretation({ nodeId: node.id, field }, event => {
      if (event.type === 'meta') {
        setConfidence(event.data.confidence);
        setAuthority(event.data.authority);
        setSnapshotId(event.data.snapshotId);
      } else if (event.type === 'delta') {
        setNarrative(current => ({ ...current, [event.data.section]: `${current[event.data.section]}${event.data.text}` }));
      } else if (event.type === 'evidence') {
        setEvidence(current => current.some(item => item.id === event.data.id) ? current : [...current, event.data]);
      } else if (event.type === 'improvement') {
        setImprovements(current => current.some(item => item.id === event.data.id) ? current : [...current, event.data]);
      } else if (event.type === 'done') {
        setAuthority(event.data.authority);
        setStatus('done');
      }
    }, controller.signal).catch(requestError => {
      if ((requestError as Error).name === 'AbortError') return;
      setError(requestError instanceof Error ? requestError.message : String(requestError));
      setStatus('error');
    });
    return () => controller.abort();
  }, [node, field]);

  const citedEvidence = useMemo(() => evidence.slice(0, 4), [evidence]);

  if (!node) return <div className="lineage-ai-empty"><RobotOutlined /><p>Select a node to generate an evidence-bound interpretation.</p></div>;

  return (
    <div className="lineage-ai-inspector" aria-live="polite">
      <header>
        <span><RobotOutlined /> AI EVIDENCE INTERPRETATION</span>
        <em className={`is-${authority?.status ?? 'pending'}`}>
          {status === 'streaming' ? <><LoadingOutlined spin /> Streaming</> : authority?.status === 'generated' ? 'AI generated' : authority?.status === 'fallback' ? 'Deterministic fallback' : 'Preparing'}
        </em>
      </header>
      <div className="lineage-ai-scope">
        <small>Current scope</small>
        <strong>{field ? `${node.label} · ${field}` : `${node.label} · All used fields`}</strong>
      </div>
      <div className="lineage-ai-field-switch">
        <span>Compare field paths</span>
        <div>
          <button type="button" className={field === null ? 'is-active' : ''} aria-pressed={field === null} aria-label="Interpret all fields" onClick={() => onFieldChange(null)}>All fields</button>
          {node.fields.map(item => <button type="button" key={item} className={field === item ? 'is-active' : ''} aria-pressed={field === item} aria-label={`Interpret field ${item}`} onClick={() => onFieldChange(item)}>{item}</button>)}
        </div>
      </div>

      {confidence ? (
        <section className={`lineage-confidence-card is-${confidence.level}`}>
          <div className="lineage-confidence-score"><strong>{confidence.score}</strong><span>%</span></div>
          <div><span><SafetyCertificateOutlined /> {confidence.label}</span><strong>{confidence.level.toUpperCase()}</strong><small>Deterministic coverage — not AI probability</small></div>
        </section>
      ) : <div className="lineage-ai-skeleton"><i /><i /><i /></div>}

      {narrative.summary ? (
        <section className="lineage-ai-narrative">
          <h3>What this path means</h3>
          <p>{narrative.summary}<span className={status === 'streaming' ? 'lineage-stream-cursor' : ''} /></p>
          {narrative.basis ? <><h3>Why this path exists</h3><p>{narrative.basis}</p></> : null}
          {narrative.confidence ? <><h3>How to read confidence</h3><p>{narrative.confidence}</p></> : null}
        </section>
      ) : null}

      {confidence ? (
        <section className="lineage-confidence-drivers">
          <h3><CheckCircleFilled /> Confidence drivers</h3>
          {confidence.drivers.map(driver => <span key={driver}>{driver}</span>)}
          <small>{confidence.methodology}</small>
        </section>
      ) : null}

      {improvements.length ? (
        <section className="lineage-ai-improvements">
          <h3><BulbOutlined /> Improve this lineage</h3>
          {improvements.map(item => <article key={item.id} className={`is-${item.priority}`}><em>{item.priority}</em><strong>{item.title}</strong><p>{item.why}</p><small>{item.action}</small></article>)}
        </section>
      ) : null}

      {citedEvidence.length ? (
        <section className="lineage-ai-citations">
          <h3><FileSearchOutlined /> Cited path evidence</h3>
          {citedEvidence.map(item => <article key={item.id}><span>{item.provider}</span><p>{item.statement}</p><code>{item.locator}</code></article>)}
        </section>
      ) : null}

      {error ? <div className="lineage-ai-error">AI interpretation unavailable: {error}</div> : null}
      {authority ? <footer><span>Graph authority: server-projected</span><small>{authority.model} · {snapshotId}</small></footer> : null}
    </div>
  );
};
